import React, { useState, useEffect, useMemo, useCallback } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { auth } from '../firebase';
import {
  Activity, Plus, TrendingUp, Clock, Trash2, Loader2, Lock,
  ChevronRight, AlertCircle, AlertTriangle, User, Heart,
  Save, FileDown, Calendar, Scale, Thermometer, Droplets,
  Zap, Info, CheckCircle2, History, ShieldAlert, Target, XCircle
} from 'lucide-react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { healthRecordService } from '../Services/healthRecordService';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer
} from 'recharts';


const HealthRecordPage = () => {
  const { hasPremiumAccess, isTrialExpired } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [records, setRecords] = useState([]);
  const [latestValues, setLatestValues] = useState({});
  const [activeTab, setActiveTab] = useState('blood_pressure');
  const [activeSection, setActiveSection] = useState('measures'); 
  const [showAddModal, setShowAddModal] = useState(false);
  const [glucoseUnit, setGlucoseUnit] = useState('mg/dL');
  const [addingRecord, setAddingRecord] = useState(false);

  // Form inputs pour listes
  const [newAlertInfo, setNewAlertInfo] = useState('');
  const [newMedicalHistory, setNewMedicalHistory] = useState('');
  const [newPersonalNote, setNewPersonalNote] = useState('');

  // Formulaire mesures
  const [formType, setFormType] = useState('blood_pressure');
  const [formValue, setFormValue] = useState('');
  const [formValue2, setFormValue2] = useState('');
  const [formDate, setFormDate] = useState(new Date().toISOString().slice(0, 16));
  const [formNotes, setFormNotes] = useState('');

  // Profil
  const [savingProfile, setSavingProfile] = useState(false);
  const [healthProfile, setHealthProfile] = useState({
    birthDate: '',
    gender: '',
    height: '',
    baseWeight: '',
    bloodType: '',
    medicalHistory: [],
    alertInfo: [],
    personalNotes: [],
    smoker: 'non',
    alcohol: 'non',
    physicalActivity: 'modere',
  });

  const RECORD_TYPES = healthRecordService.RECORD_TYPES;

  // --- LOGIQUE EXPORT PDF ---
  const handleExportPDF = async () => {
    let logoDataUrl = null;
    try {
      const res = await fetch('/RemaH.png');
      const blob = await res.blob();
      logoDataUrl = await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.readAsDataURL(blob);
      });
    } catch (_) {}
    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const pageW = doc.internal.pageSize.getWidth();
    const margin = 15;
    const contentW = pageW - margin * 2;
    let y = 0;

    const addPage = () => { doc.addPage(); y = 20; };
    const checkPageBreak = (needed = 10) => { if (y + needed > 275) addPage(); };
    const sectionTitle = (title, color = [30, 120, 80]) => {
      checkPageBreak(12);
      doc.setFillColor(...color);
      doc.roundedRect(margin, y, contentW, 9, 2, 2, 'F');
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(11);
      doc.setTextColor(255, 255, 255);
      doc.text(title, margin + 4, y + 6.2);
      doc.setTextColor(0, 0, 0);
      y += 13;
    };

    const fieldRow = (label, value) => {
      checkPageBreak(7);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9);
      doc.setTextColor(80, 80, 80);
      doc.text(label + ' :', margin + 2, y);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(20, 20, 20);
      doc.text(value || '—', margin + 55, y);
      y += 6;
    };

    const tagList = (items) => {
      if (!items || items.length === 0) {
        checkPageBreak(6);
        doc.setFont('helvetica', 'italic');
        doc.setFontSize(9);
        doc.setTextColor(150, 150, 150);
        doc.text('Aucun élément renseigné', margin + 4, y);
        y += 6;
        return;
      }
      items.forEach((item) => {
        checkPageBreak(6);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(9);
        doc.setTextColor(40, 40, 40);
        doc.text(`• ${item}`, margin + 4, y);
        y += 5.5;
      });
      y += 2;
    };

    doc.setFillColor(30, 120, 80);
    doc.rect(0, 0, pageW, 28, 'F');
    doc.setFont('helvetica', 'bold'); doc.setFontSize(18); doc.setTextColor(255, 255, 255);
    doc.text('Dossier Santé Patient', margin, 13);
    doc.setFont('helvetica', 'normal'); doc.setFontSize(9);
    doc.text(`Exporté le ${new Date().toLocaleDateString('fr-FR')}`, margin, 20);
    if (logoDataUrl) {
      doc.setFillColor(255, 255, 255); doc.roundedRect(pageW - margin - 24, 3, 24, 22, 3, 3, 'F');
      doc.addImage(logoDataUrl, 'PNG', pageW - margin - 23, 4, 22, 20);
    }
    y = 35;
    sectionTitle('1. Profil de santé', [30, 120, 80]);
    fieldRow('Date de naissance', healthProfile.birthDate || '—');
    fieldRow('Sexe', healthProfile.gender || '—');
    fieldRow('Groupe sanguin', healthProfile.bloodType || '—');
    fieldRow('Taille', healthProfile.height ? `${healthProfile.height} cm` : '—');
    fieldRow('Poids de référence', healthProfile.baseWeight ? `${healthProfile.baseWeight} kg` : '—');
    y += 10;
    sectionTitle('2. Dernières mesures', [37, 99, 235]);
    const latestRows = Object.entries(RECORD_TYPES).map(([type, config]) => {
      const latest = latestValues[type];
      return [config.label, latest ? healthRecordService.formatValue(type, latest.value, latest.value2) : '—', latest ? healthRecordService.formatDate(latest.measuredAt) : '—'];
    });
    autoTable(doc, { startY: y, head: [['Indicateur', 'Valeur', 'Date']], body: latestRows, headStyles: { fillColor: [37, 99, 235] } });
    doc.save(`dossier-sante-${new Date().toISOString().slice(0,10)}.pdf`);
    toast.success('PDF exporté avec succès');
  };

  // --- LOGIQUE DE DONNÉES ---
  useEffect(() => {
    if (hasPremiumAccess) {
      loadData();
      loadHealthProfile();
    } else {
      setLoading(false);
    }
  }, [hasPremiumAccess]);

  const loadData = async () => {
    setLoading(true);
    try {
      const allRecords = await healthRecordService.getRecords(null, 200);
      setRecords(allRecords);

      // Dériver les dernières valeurs directement depuis les données chargées
      // au lieu de faire 6 appels API séparés (source de pannes silencieuses)
      const latest = {};
      for (const type of Object.keys(RECORD_TYPES)) {
        const typeRecords = allRecords
          .filter(r => r.type === type)
          .sort((a, b) => b.measuredAt - a.measuredAt);
        latest[type] = typeRecords[0] || null;
      }
      setLatestValues(latest);
    } catch (error) { toast.error('Erreur chargement'); }
    finally { setLoading(false); }
  };

  const loadHealthProfile = async () => {
    try {
      const token = await auth.currentUser.getIdToken();
      const response = await fetch('https://europe-west1-medaviceia.cloudfunctions.net/getHealthProfileV2', {
        method: 'GET', headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        if (data.profile) setHealthProfile(prev => ({ ...prev, ...data.profile }));
      }
    } catch (e) {}
  };

  const handleSaveProfile = async () => {
    setSavingProfile(true);
    try {
      const token = await auth.currentUser.getIdToken();
      const response = await fetch('https://europe-west1-medaviceia.cloudfunctions.net/saveHealthProfileV2', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ profile: healthProfile }),
      });
      if (response.ok) toast.success('Profil santé enregistré');
    } catch (e) { toast.error('Erreur sauvegarde'); }
    finally { setSavingProfile(false); }
  };

  const handleAddRecord = async (e) => {
    e.preventDefault();
    let valueToSave = parseFloat(formValue);
    if (formType === 'glucose' && glucoseUnit === 'g/L') valueToSave = Math.round(valueToSave * 100);
    setAddingRecord(true);
    try {
      await healthRecordService.addRecord(formType, valueToSave, RECORD_TYPES[formType].hasSecondValue ? formValue2 : null, new Date(formDate), formNotes || null);
      toast.success('Mesure ajoutée');
      setShowAddModal(false); setFormValue(''); setFormValue2(''); setFormNotes('');
      loadData();
    } catch (e) { toast.error(e.message); }
    finally { setAddingRecord(false); }
  };

  const handleDeleteRecord = async (id) => {
    if (!window.confirm('Supprimer cette mesure ?')) return;
    try {
      await healthRecordService.deleteRecord(id);
      toast.success('Mesure supprimée');
      loadData();
    } catch (e) { toast.error(e.message); }
  };

  const addToList = (field, value, setValue) => {
    if (!value.trim()) return;
    setHealthProfile(prev => ({ ...prev, [field]: [...(prev[field] || []), value.trim()] }));
    setValue('');
  };

  const removeFromList = (field, index) => {
    setHealthProfile(prev => ({ ...prev, [field]: prev[field].filter((_, i) => i !== index) }));
  };

  const activeConfig = RECORD_TYPES[activeTab];
  const chartData = useMemo(() => records
    .filter((r) => r.type === activeTab)
    .sort((a, b) => a.measuredAt - b.measuredAt)
    .slice(-15)
    .map((r) => ({
      date: new Date(r.measuredAt).toLocaleString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }),
      fullDate: new Date(r.measuredAt).toLocaleString('fr-FR', { day: '2-digit', month: 'long', hour: '2-digit', minute: '2-digit' }),
      value: r.value,
      value2: r.value2,
      notes: r.notes || null,
      unit: activeConfig.unit,
      color: activeConfig.color,
      hasSecondValue: activeConfig.hasSecondValue,
    })), [records, activeTab]);

  const CustomTooltip = useCallback(({ active, payload }) => {
    if (!active || !payload?.length) return null;
    // payload[0].value = valeur réelle de la barre survolée (injectée par Recharts)
    // payload[0].payload = métadonnées de la ligne (date, notes, unité, couleur)
    const value  = payload[0].value;
    const value2 = payload[1]?.value ?? null;
    const meta   = payload[0].payload;
    return (
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-4 min-w-[180px] border border-gray-100 dark:border-gray-700">
        <p className="text-[10px] font-black uppercase tracking-widest text-gray-400 mb-2">{meta.fullDate}</p>
        <div className="flex items-baseline gap-1 mb-1">
          <span className="text-2xl font-black" style={{ color: meta.color }}>{value}</span>
          {meta.hasSecondValue && value2 != null && (
            <span className="text-2xl font-black text-blue-400">/{value2}</span>
          )}
          <span className="text-xs font-bold text-gray-400 ml-1">{meta.unit}</span>
        </div>
        {meta.notes && (
          <p className="text-xs text-gray-500 dark:text-gray-400 italic border-t border-gray-100 dark:border-gray-700 pt-2 mt-2">
            💬 {meta.notes}
          </p>
        )}
      </div>
    );
  }, [activeTab, chartData]);


  if (!hasPremiumAccess) {
    return (
      <DashboardLayout>
        <div className="max-w-4xl mx-auto px-4 py-20">
          <Card className="p-12 text-center bg-gray-900 text-white border-none shadow-2xl rounded-[40px] relative overflow-hidden">
            <div className="absolute top-0 right-0 p-10 opacity-10"><Lock size={200} /></div>
            <Lock className="w-20 h-20 text-emerald-400 mx-auto mb-6" />
            <h2 className="text-4xl font-black mb-4 uppercase tracking-tighter">
              {isTrialExpired ? 'Votre essai est terminé' : 'Espace Premium'}
            </h2>
            <p className="text-gray-400 mb-10 max-w-sm mx-auto">
              {isTrialExpired
                ? 'Votre essai de 7 jours est terminé. Abonnez-vous pour retrouver votre Carnet de Santé — vos données sont conservées.'
                : 'Le Carnet de Santé intelligent est réservé à nos membres. Suivez vos constantes et visualisez vos progrès.'}
            </p>
            <Button onClick={() => navigate('/pricing')} className="bg-emerald-500 hover:bg-emerald-600 text-white px-10 py-8 text-xl rounded-2xl shadow-xl transition-transform hover:scale-105">
              Débloquer mon dossier <ChevronRight className="ml-2" />
            </Button>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  if (loading) return (
    <DashboardLayout>
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="animate-spin text-emerald-500 w-12 h-12" />
      </div>
    </DashboardLayout>
  );

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto px-4 py-8 space-y-10">
        
        {/* --- HEADER --- */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div className="flex items-center gap-5">
            <div className="w-16 h-16 bg-gradient-to-tr from-emerald-600 to-teal-400 rounded-3xl flex items-center justify-center shadow-2xl shadow-emerald-500/20 rotate-3">
              <Activity className="w-9 h-9 text-white" />
            </div>
            <div>
              <h1 className="text-4xl font-black tracking-tight text-gray-900 dark:text-white">Carnet de Santé</h1>
              <div className="flex items-center gap-2 text-emerald-600 font-bold uppercase text-[10px] tracking-widest mt-1">
                <Lock size={12} /> Données chiffrées & Sécurisées
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <Button onClick={handleExportPDF} variant="outline" className="h-14 px-6 rounded-2xl border-emerald-100 dark:border-emerald-900 bg-white dark:bg-gray-900 hover:bg-emerald-50 text-gray-700 dark:text-gray-200 transition-all">
              <FileDown className="w-5 h-5 mr-2 text-emerald-600" /> Export Dossier
            </Button>
            {activeSection === 'measures' && (
              <Button onClick={() => setShowAddModal(true)} className="h-14 px-8 bg-emerald-600 hover:bg-emerald-700 text-white rounded-2xl shadow-lg shadow-emerald-500/25 transition-all">
                <Plus className="w-5 h-5 mr-2" /> Nouvelle mesure
              </Button>
            )}
          </div>
        </div>

        {/* --- NAVIGATION --- */}
        <div className="flex p-1.5 bg-gray-100 dark:bg-gray-800 rounded-2xl w-fit">
          <button 
            onClick={() => setActiveSection('measures')}
            className={`flex items-center gap-2 px-6 py-3 rounded-xl font-bold transition-all ${activeSection === 'measures' ? 'bg-white dark:bg-gray-700 shadow-sm text-emerald-600' : 'text-gray-500 hover:text-gray-700'}`}
          >
            <Heart size={18} /> Mesures
          </button>
          <button 
            onClick={() => setActiveSection('profile')}
            className={`flex items-center gap-2 px-6 py-3 rounded-xl font-bold transition-all ${activeSection === 'profile' ? 'bg-white dark:bg-gray-700 shadow-sm text-emerald-600' : 'text-gray-500 hover:text-gray-700'}`}
          >
            <User size={18} /> Profil Médical
          </button>
        </div>

        {/* ==================== SECTION MESURES ==================== */}
        {activeSection === 'measures' && (
          <div className="space-y-8 animate-in fade-in duration-500">
            {/* Quick Metrics Bento */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {Object.entries(RECORD_TYPES).map(([type, config]) => {
                const latest = latestValues[type];
                const isActive = activeTab === type;
                return (
                  <Card
                    key={type}
                    onClick={() => setActiveTab(type)}
                    className={`relative p-5 cursor-pointer transition-all border-none overflow-hidden group ${isActive ? 'shadow-xl scale-105 z-10' : 'hover:bg-gray-50 dark:hover:bg-gray-800'}`}
                  >
                    {isActive && <div className="absolute inset-0 opacity-10" style={{ backgroundColor: config.color }} />}
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-4 transition-colors ${isActive ? 'text-white' : 'bg-gray-100 dark:bg-gray-800 text-gray-400'}`} style={{ backgroundColor: isActive ? config.color : undefined }}>
                      {config.icon}
                    </div>
                    <p className="text-[10px] font-black uppercase tracking-widest text-gray-400 mb-1">{config.label}</p>
                    <div className={`text-lg font-black ${isActive ? 'text-gray-900 dark:text-white' : 'text-gray-500'}`}>
                      {latest ? healthRecordService.formatValue(type, latest.value, latest.value2) : '--'}
                    </div>
                  </Card>
                );
              })}
            </div>

            <div className="grid lg:grid-cols-12 gap-8">
              {/* Analytics Center */}
              <Card className="lg:col-span-8 p-8 border-none shadow-sm rounded-[32px] bg-white dark:bg-gray-900">
                <div className="flex items-center justify-between mb-10">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-emerald-50 dark:bg-emerald-950 rounded-lg text-emerald-600"><TrendingUp size={20} /></div>
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white">Analyse Graphique</h3>
                  </div>
                  <div className="text-xs font-bold text-gray-400 uppercase tracking-widest">
                    {RECORD_TYPES[activeTab].label}
                  </div>
                </div>

                <div className="h-[350px] w-full">
                  {chartData.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={chartData} barCategoryGap="35%" barGap={4}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                        <XAxis dataKey="date" axisLine={false} tickLine={false} tick={{ fontSize: 10, fontWeight: 'bold' }} dy={10} />
                        <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 10, fontWeight: 'bold' }} />
                        <Tooltip content={CustomTooltip} cursor={{ fill: 'rgba(0,0,0,0.04)', radius: 8 }} />
                        <Bar dataKey="value" name={RECORD_TYPES[activeTab].label} fill={RECORD_TYPES[activeTab].color} radius={[8, 8, 0, 0]} maxBarSize={48} isAnimationActive={false} />
                        {RECORD_TYPES[activeTab].hasSecondValue && (
                          <Bar dataKey="value2" name="Diastolique" fill="#60a5fa" radius={[8, 8, 0, 0]} maxBarSize={48} isAnimationActive={false} />
                        )}
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-full flex flex-col items-center justify-center text-gray-300 italic">
                      <History size={48} className="mb-4 opacity-20" />
                      En attente de données pour générer l'analyse...
                    </div>
                  )}
                </div>
              </Card>

              {/* Recent History Sidebar */}
              <Card className="lg:col-span-4 p-8 border-none shadow-sm rounded-[32px] bg-white dark:bg-gray-900 overflow-hidden">
                <h3 className="text-xl font-bold mb-8 flex items-center gap-2 text-gray-900 dark:text-white">
                  <Clock className="text-emerald-500" size={20} /> Journal
                </h3>
                <div className="space-y-4 max-h-[400px] overflow-y-auto pr-2 scrollbar-hide">
                  {records.length > 0 ? records.slice(0, 15).map((record) => (
                    <div key={record.id} className="group flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800/50 rounded-2xl transition-all hover:bg-white dark:hover:bg-gray-800 border border-transparent hover:border-gray-100">
                      <div className="flex items-center gap-4">
                        <div className="text-xl">{RECORD_TYPES[record.type].icon}</div>
                        <div>
                          <div className="font-black text-sm text-gray-900 dark:text-white">
                            {healthRecordService.formatValue(record.type, record.value, record.value2)}
                          </div>
                          <div className="text-[10px] text-gray-400 font-bold uppercase">{healthRecordService.formatDate(record.measuredAt)}</div>
                        </div>
                      </div>
                      <button onClick={() => handleDeleteRecord(record.id)} className="opacity-0 group-hover:opacity-100 p-2 text-rose-500 hover:bg-rose-50 rounded-lg transition-all">
                        <Trash2 size={16} />
                      </button>
                    </div>
                  )) : <p className="text-center py-10 text-gray-400 text-sm">Aucune mesure.</p>}
                </div>
              </Card>
            </div>
          </div>
        )}

        {/* ==================== SECTION PROFIL ==================== */}
        {activeSection === 'profile' && (
          <div className="space-y-6 animate-in slide-in-from-right-10 duration-500">
            
            {/* Warning Banner */}
            <div className="flex items-start gap-4 p-5 bg-blue-50 dark:bg-blue-950/30 rounded-[24px] border border-blue-100 dark:border-blue-900">
              <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-full mt-1"><AlertCircle className="w-5 h-5 text-blue-600" /></div>
              <div>
                <p className="text-sm text-blue-800 dark:text-blue-200 font-medium">Ces informations permettent à l'assistant IA de mieux vous accompagner et d'adapter ses réponses à votre profil. Elles sont chiffrées et strictement confidentielles.</p>
                <p className="text-xs text-blue-600/70 dark:text-blue-400/70 mt-2 flex items-center gap-1"><AlertTriangle size={14}/> Les informations sont fournies à titre indicatif uniquement et ne remplacent pas un professionnel de santé.</p>
              </div>
            </div>

            <div className="grid lg:grid-cols-2 gap-6">
              {/* CARD 1: Biométrie */}
              <Card className="p-8 border-none shadow-sm rounded-[32px] bg-white dark:bg-gray-900">
                <h3 className="text-xl font-black mb-6 flex items-center gap-3 text-gray-900 dark:text-white">
                  <div className="p-2 bg-blue-100 text-blue-600 rounded-lg"><Scale size={18} /></div>
                  Biométrie & État civil
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-[10px] font-black uppercase text-gray-400 ml-1">Date de naissance</Label>
                    <input type="date" value={healthProfile.birthDate} onChange={(e) => setHealthProfile(p => ({...p, birthDate: e.target.value}))} className="w-full h-12 px-4 bg-gray-50 dark:bg-gray-800 border-none rounded-xl outline-none text-gray-900 dark:text-white" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[10px] font-black uppercase text-gray-400 ml-1">Sexe</Label>
                    <select value={healthProfile.gender} onChange={(e) => setHealthProfile(p => ({...p, gender: e.target.value}))} className="w-full h-12 px-4 bg-gray-50 dark:bg-gray-800 border-none rounded-xl outline-none text-gray-900 dark:text-white">
                      <option value="">Non renseigné</option><option value="homme">Homme</option><option value="femme">Femme</option><option value="autre">Autre</option>
                    </select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[10px] font-black uppercase text-gray-400 ml-1">Taille (cm)</Label>
                    <input type="number" placeholder="Ex: 175" value={healthProfile.height} onChange={(e) => setHealthProfile(p => ({...p, height: e.target.value}))} className="w-full h-12 px-4 bg-gray-50 dark:bg-gray-800 border-none rounded-xl outline-none text-gray-900 dark:text-white" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[10px] font-black uppercase text-gray-400 ml-1">Poids réf. (kg)</Label>
                    <input type="number" step="0.1" placeholder="Ex: 70" value={healthProfile.baseWeight} onChange={(e) => setHealthProfile(p => ({...p, baseWeight: e.target.value}))} className="w-full h-12 px-4 bg-gray-50 dark:bg-gray-800 border-none rounded-xl outline-none text-gray-900 dark:text-white" />
                  </div>
                  <div className="space-y-2 col-span-2">
                    <Label className="text-[10px] font-black uppercase text-gray-400 ml-1">Groupe Sanguin</Label>
                    <select value={healthProfile.bloodType} onChange={(e) => setHealthProfile(p => ({...p, bloodType: e.target.value}))} className="w-full h-12 px-4 bg-gray-50 dark:bg-gray-800 border-none rounded-xl outline-none text-gray-900 dark:text-white">
                      <option value="">Sélectionner...</option>
                      {['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'].map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                </div>
              </Card>

              {/* CARD 2: Mode de vie */}
              <Card className="p-8 border-none shadow-sm rounded-[32px] bg-white dark:bg-gray-900">
                <h3 className="text-xl font-black mb-6 flex items-center gap-3 text-gray-900 dark:text-white">
                  <div className="p-2 bg-orange-100 text-orange-600 rounded-lg"><Zap size={18} /></div>
                  Mode de vie
                </h3>
                <div className="space-y-5">
                  <div className="space-y-2">
                    <Label className="text-[10px] font-black uppercase text-gray-400 ml-1">Tabagisme</Label>
                    <select value={healthProfile.smoker} onChange={(e) => setHealthProfile(p => ({...p, smoker: e.target.value}))} className="w-full h-12 px-4 bg-gray-50 dark:bg-gray-800 border-none rounded-xl outline-none text-gray-900 dark:text-white">
                      <option value="non">Non-fumeur</option><option value="ancien">Ancien fumeur</option><option value="occasionnel">Occasionnel</option><option value="quotidien">Quotidien</option>
                    </select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[10px] font-black uppercase text-gray-400 ml-1">Consommation d'alcool</Label>
                    <select value={healthProfile.alcohol} onChange={(e) => setHealthProfile(p => ({...p, alcohol: e.target.value}))} className="w-full h-12 px-4 bg-gray-50 dark:bg-gray-800 border-none rounded-xl outline-none text-gray-900 dark:text-white">
                      <option value="non">Jamais</option><option value="occasionnel">Occasionnel</option><option value="regulier">Régulier</option>
                    </select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[10px] font-black uppercase text-gray-400 ml-1">Activité Physique</Label>
                    <select value={healthProfile.physicalActivity} onChange={(e) => setHealthProfile(p => ({...p, physicalActivity: e.target.value}))} className="w-full h-12 px-4 bg-gray-50 dark:bg-gray-800 border-none rounded-xl outline-none text-gray-900 dark:text-white">
                      <option value="sedentaire">Sédentaire</option><option value="leger">Léger (1-2x/semaine)</option><option value="modere">Modéré (3-4x/semaine)</option><option value="actif">Actif (5+/semaine)</option><option value="tres_actif">Très actif / Sportif</option>
                    </select>
                  </div>
                </div>
              </Card>

              {/* CARD 3: Antécédents */}
              <Card className="p-8 border-none shadow-sm rounded-[32px] bg-white dark:bg-gray-900">
                <h3 className="text-xl font-black mb-2 flex items-center gap-3 text-gray-900 dark:text-white">
                  <div className="p-2 bg-purple-100 text-purple-600 rounded-lg"><History size={18} /></div>
                  Antécédents & Contexte
                </h3>
                <p className="text-xs text-gray-500 mb-6">Éléments de votre dossier (ex: diabète familial, opération récente...)</p>
                <div className="space-y-4">
                  <div className="flex gap-2">
                    <input value={newMedicalHistory} onChange={(e) => setNewMedicalHistory(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && addToList('medicalHistory', newMedicalHistory, setNewMedicalHistory)} className="flex-1 h-12 px-4 bg-gray-50 dark:bg-gray-800 border-none rounded-xl outline-none text-sm text-gray-900 dark:text-white placeholder:text-gray-400" placeholder="Ajouter un antécédent..." />
                    <Button onClick={() => addToList('medicalHistory', newMedicalHistory, setNewMedicalHistory)} className="h-12 w-12 rounded-xl bg-purple-600 text-white hover:bg-purple-700"><Plus size={18} /></Button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {healthProfile.medicalHistory?.map((item, idx) => (
                      <span key={idx} className="px-3 py-1.5 bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded-lg text-xs font-bold flex items-center gap-2">
                        {item} <Trash2 size={12} className="cursor-pointer hover:text-red-500" onClick={() => removeFromList('medicalHistory', idx)} />
                      </span>
                    ))}
                    {(!healthProfile.medicalHistory || healthProfile.medicalHistory.length === 0) && <span className="text-xs text-gray-400 italic">Aucun antécédent renseigné</span>}
                  </div>
                </div>
              </Card>

              {/* CARD 4: Précautions */}
              <Card className="p-8 border-none shadow-sm rounded-[32px] bg-white dark:bg-gray-900">
                <h3 className="text-xl font-black mb-2 flex items-center gap-3 text-gray-900 dark:text-white">
                  <div className="p-2 bg-rose-100 text-rose-600 rounded-lg"><ShieldAlert size={18} /></div>
                  Précautions & Tolérances
                </h3>
                <p className="text-xs text-gray-500 mb-6">Vos sensibilités ou points de vigilance (ex: lactose, genou fragile...)</p>
                <div className="space-y-4">
                  <div className="flex gap-2">
                    <input value={newAlertInfo} onChange={(e) => setNewAlertInfo(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && addToList('alertInfo', newAlertInfo, setNewAlertInfo)} className="flex-1 h-12 px-4 bg-gray-50 dark:bg-gray-800 border-none rounded-xl outline-none text-sm text-gray-900 dark:text-white placeholder:text-gray-400" placeholder="Ajouter une précaution..." />
                    <Button onClick={() => addToList('alertInfo', newAlertInfo, setNewAlertInfo)} className="h-12 w-12 rounded-xl bg-rose-600 text-white hover:bg-rose-700"><Plus size={18} /></Button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {healthProfile.alertInfo?.map((item, idx) => (
                      <span key={idx} className="px-3 py-1.5 bg-rose-50 dark:bg-rose-900/30 text-rose-700 dark:text-rose-300 rounded-lg text-xs font-bold flex items-center gap-2">
                        {item} <Trash2 size={12} className="cursor-pointer hover:text-rose-900" onClick={() => removeFromList('alertInfo', idx)} />
                      </span>
                    ))}
                    {(!healthProfile.alertInfo || healthProfile.alertInfo.length === 0) && <span className="text-xs text-gray-400 italic">Aucune précaution renseignée</span>}
                  </div>
                </div>
              </Card>

              {/* CARD 5: Objectifs Bien-être */}
              <Card className="lg:col-span-2 p-8 border-none shadow-sm rounded-[32px] bg-white dark:bg-gray-900">
                <h3 className="text-xl font-black mb-2 flex items-center gap-3 text-gray-900 dark:text-white">
                  <div className="p-2 bg-emerald-100 text-emerald-600 rounded-lg"><Target size={18} /></div>
                  Objectifs & Agenda Bien-être
                </h3>
                <p className="text-xs text-gray-500 mb-6">Vos rappels, résolutions ou notes personnelles (ex: RDV ostéo, boire 2L d'eau...)</p>
                <div className="space-y-4">
                  <div className="flex gap-2">
                    <input value={newPersonalNote} onChange={(e) => setNewPersonalNote(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && addToList('personalNotes', newPersonalNote, setNewPersonalNote)} className="flex-1 h-12 px-4 bg-gray-50 dark:bg-gray-800 border-none rounded-xl outline-none text-sm text-gray-900 dark:text-white placeholder:text-gray-400" placeholder="Ajouter une note ou un objectif..." />
                    <Button onClick={() => addToList('personalNotes', newPersonalNote, setNewPersonalNote)} className="h-12 px-6 rounded-xl bg-emerald-600 text-white hover:bg-emerald-700"><Plus size={18} className="mr-2" /> Ajouter</Button>
                  </div>
                  <div className="flex flex-wrap gap-2 mt-4">
                    {healthProfile.personalNotes?.map((item, idx) => (
                      <span key={idx} className="px-4 py-2 bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 rounded-xl text-sm font-bold flex items-center gap-2">
                        {item} <Trash2 size={14} className="cursor-pointer hover:text-red-500 ml-2" onClick={() => removeFromList('personalNotes', idx)} />
                      </span>
                    ))}
                    {(!healthProfile.personalNotes || healthProfile.personalNotes.length === 0) && <span className="text-xs text-gray-400 italic">Aucun objectif renseigné</span>}
                  </div>
                </div>
              </Card>

            </div>

            <div className="flex justify-end pt-4">
              <Button onClick={handleSaveProfile} disabled={savingProfile} className="h-16 px-10 bg-gray-900 dark:bg-white text-white dark:text-black rounded-2xl font-black shadow-xl hover:scale-105 transition-all">
                {savingProfile ? <Loader2 className="animate-spin mr-2" /> : <Save className="mr-2" />} Enregistrer mon profil
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* --- MODAL AJOUT MESURE --- */}
      {showAddModal && (
        <div className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm flex items-center justify-center z-[100] p-4 animate-in fade-in duration-200">
          <Card className="w-full max-w-md bg-white dark:bg-gray-950 p-8 rounded-[40px] shadow-2xl border-none">
            <div className="flex justify-between items-center mb-8">
              <h2 className="text-3xl font-black tracking-tighter text-gray-900 dark:text-white">Nouveau relevé</h2>
              <button onClick={() => setShowAddModal(false)} className="w-10 h-10 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center"><XCircle className="w-5 h-5" /></button>
            </div>
            
            <form onSubmit={handleAddRecord} className="space-y-6">
              <div className="space-y-2">
                <Label className="text-[10px] font-black uppercase text-gray-400 ml-1">Paramètre</Label>
                <select value={formType} onChange={(e) => setFormType(e.target.value)} className="w-full h-14 px-5 bg-gray-50 dark:bg-gray-900 rounded-2xl outline-none font-bold text-gray-900 dark:text-white">
                  {Object.entries(RECORD_TYPES).map(([k,v]) => <option key={k} value={k}>{v.label}</option>)}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-[10px] font-black uppercase text-gray-400 ml-1">Mesure</Label>
                  <input type="number" step="0.1" value={formValue} onChange={(e) => setFormValue(e.target.value)} className="w-full h-14 px-5 bg-gray-50 dark:bg-gray-900 rounded-2xl border-none outline-none text-xl font-bold text-gray-900 dark:text-white" required />
                </div>
                {RECORD_TYPES[formType].hasSecondValue && (
                  <div className="space-y-2">
                    <Label className="text-[10px] font-black uppercase text-gray-400 ml-1">Diastolique</Label>
                    <input type="number" step="0.1" value={formValue2} onChange={(e) => setFormValue2(e.target.value)} className="w-full h-14 px-5 bg-gray-50 dark:bg-gray-900 rounded-2xl border-none outline-none text-xl font-bold text-gray-900 dark:text-white" required />
                  </div>
                )}
                {formType === 'glucose' && (
                  <div className="space-y-2">
                    <Label className="text-[10px] font-black uppercase text-gray-400 ml-1">Unité</Label>
                    <select value={glucoseUnit} onChange={(e) => setGlucoseUnit(e.target.value)} className="w-full h-14 px-4 bg-gray-50 dark:bg-gray-900 rounded-2xl outline-none font-bold text-gray-900 dark:text-white">
                      <option value="mg/dL">mg/dL</option><option value="g/L">g/L</option>
                    </select>
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <Label className="text-[10px] font-black uppercase text-gray-400 ml-1">Date & Heure</Label>
                <input type="datetime-local" value={formDate} onChange={(e) => setFormDate(e.target.value)} className="w-full h-14 px-5 bg-gray-50 dark:bg-gray-900 rounded-2xl border-none outline-none font-medium text-gray-900 dark:text-white" />
              </div>

              <div className="space-y-2">
                <Label className="text-[10px] font-black uppercase text-gray-400 ml-1">Commentaire (optionnel)</Label>
                <textarea
                  value={formNotes}
                  onChange={(e) => setFormNotes(e.target.value)}
                  placeholder="Ex : pris après l'effort, à jeun, au réveil..."
                  rows={2}
                  className="w-full px-5 py-3 bg-gray-50 dark:bg-gray-900 rounded-2xl border-none outline-none resize-none text-sm text-gray-900 dark:text-white placeholder:text-gray-400 focus:ring-2 focus:ring-emerald-500 transition-all"
                />
              </div>

              <Button type="submit" disabled={addingRecord} className="w-full h-16 bg-emerald-600 hover:bg-emerald-700 text-white rounded-2xl text-lg font-bold shadow-lg shadow-emerald-500/20">
                {addingRecord ? <Loader2 className="animate-spin" /> : "Enregistrer la mesure"}
              </Button>
            </form>
          </Card>
        </div>
      )}

    </DashboardLayout>
  );
};

export default HealthRecordPage;